import React from "react";
import { Card, TextInput, IconButton, Icon, HStack } from "@astryxdesign/core";
import type { TextInputStatus } from "@astryxdesign/core";
import { X } from "lucide-react";
import styles from "./SectionCard.module.css";

export interface SectionCardProps {
  /** Ref forwarded to the card's root element (used for scroll-to targeting). */
  ref?: React.Ref<HTMLDivElement>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Validation status for the section title field (error styling + message). */
  status?: TextInputStatus;
  /** @default false */
  isDisabled?: boolean;
  /**
   * Plays a one-shot grow-in + fade entrance animation, for a section that
   * was just inserted via an `InsertZone`. The caller is responsible for
   * flipping this back to `false` after the animation completes (it does
   * not reset itself). No-ops under `prefers-reduced-motion: reduce`.
   * @default false
   */
  isEntering?: boolean;
  /**
   * When provided, renders a delete (X) control, hidden by default and
   * revealed on card hover or keyboard focus within the card.
   */
  onDelete?: () => void;
  deleteLabel?: string;
  /** Forwarded to the underlying input for scroll-to-error targeting. */
  "data-field-key"?: string;
}

/**
 * Bounded card for a section's title in the script-template editor: a
 * labeled `TextInput` (visible "Section title" label, unlike the hidden
 * label pattern elsewhere) plus a hover/focus-revealed delete control. A
 * visual sibling of `QuestionCard` — same Card variant, same delete-reveal
 * idiom — so a section reads as one step up in the same card hierarchy as
 * its questions.
 *
 * This MUST be rendered inside an Astryx <Theme> scope.
 */
const SectionCard = ({
  ref,
  value,
  onChange,
  placeholder,
  status,
  isDisabled = false,
  isEntering = false,
  onDelete,
  deleteLabel,
  ...rest
}: SectionCardProps): React.ReactElement => {
  const dataFieldKey = rest["data-field-key"];

  return (
    <div className={isEntering ? styles.enterWrapper : undefined}>
      <Card variant="default" className={styles.card} ref={ref}>
        <HStack align="start" gap={3}>
          <div className={styles.body}>
            <TextInput
              label="Section title"
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              size="lg"
              width="100%"
              isDisabled={isDisabled}
              status={status}
              data-field-key={dataFieldKey}
            />
          </div>
          {onDelete && !isDisabled && (
            <div className={styles.delete}>
              <IconButton
                icon={<Icon icon={X} color="secondary" />}
                label={deleteLabel ?? "Delete section"}
                variant="ghost"
                onClick={onDelete}
              />
            </div>
          )}
        </HStack>
      </Card>
    </div>
  );
};

export default SectionCard;
